import * as React from "react";
import { Route, RouteComponentProps, Switch } from "react-router-dom";
import { JobList } from "@app/jobs/JobList";
import { JobQueueMap } from "@app/jobs/job-queue-mapping/JobQueueMap";
import { JobQueues } from "@app/jobs/job-queues/JobQueues";
import { WeekendRunMonitoring } from "@app/operations/WeekendRunMonitoring";
import JobPerformance from "@app/operations/job-performance/JobPerformance";
import AiResponseReview from "@app/operations/ai-response-review/AiResponseReview";
import { IngestionRetry } from "@app/operations/provider-hub-ingestion-retry/IngestionRetry";
import { NotFound, Unauthorized } from "@app/shared/ErrorPages";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";
import { OperationsDashboard } from "@app/operations/Dashboard";
import { MarketNightlyRefresh } from "@app/operations/market-nightly-refresh/MarketNightlyRefresh";
import ProjectPerformancePage from "@app/operations/project-performance/ProjectPerformancePage";
import { SnowflakeDataSharing } from "@app/operations/snowflake-data-sharing/SnowflakeDataSharing";
import { SnowflakeDataSharingContextProvider } from "@app/operations/snowflake-data-sharing/SnowflakeDataSharingContext";

interface OperationsMatchRouteProps {
  clientId?: string;
  userId?: string;
}

type Props = RouteComponentProps<OperationsMatchRouteProps>;

export const OperationsRouter: React.FC<React.PropsWithChildren<Props>> = (props: Props) => {
  const user = React.useContext(UserContext);
  const featureFlags = React.useContext(FeatureFlagContext).enabledFeatureFlags;

  return (
    <Switch>
      <Route exact={true} path={`${props.match.url}/dashboard`} component={OperationsDashboard} />
      <Route exact={true} path={`${props.match.url}/jobs`} component={JobList} />
      <Route exact={true} path={`${props.match.url}/jobqueues`} component={JobQueues} />
      <Route exact={true} path={`${props.match.url}/jobqueuemap`} component={JobQueueMap} />
      <Route
        exact={true}
        path={`${props.match.url}/weekendrunmonitoring`}
        render={() => (
          user && (user.roles.includes(Roles.OPERATIONS_USER) || user.roles.includes(Roles.OPERATIONS_ADMIN)) ? (
            <WeekendRunMonitoring />
          ) : (
            <Unauthorized />
          )
        )}
      />
      {
        featureFlags.has(Features.AI_VIRTUAL_ASSISTANT) ?
          (<Route
            exact={true}
            path={`${props.match.url}/airesponsereview`}
            render={() => (
              user && (user.roles.includes(Roles.OPERATIONS_USER) || user.roles.includes(Roles.OPERATIONS_ADMIN)) ? (
                <AiResponseReview />
              ) : (
                <Unauthorized />
              )
            )} />
          ) : null
      }
      <Route
        exact={true}
        path={`${props.match.url}/job-performance`}
        render={() => (
          user && (user.roles.includes(Roles.OPERATIONS_USER) || user.roles.includes(Roles.OPERATIONS_ADMIN)) ? (
            <JobPerformance />
          ) : (
            <Unauthorized />
          )
        )}
      />
      <Route
        exact={true}
        path={`${props.match.url}/providerhubingestionretry`}
        render={() => (
          user && (user.roles.includes(Roles.OPERATIONS_USER) || user.roles.includes(Roles.OPERATIONS_ADMIN)) ? (
            <IngestionRetry />
          ) : (
            <Unauthorized />
          )
        )}
      />
      {
        featureFlags.has(Features.MARKET_NIGHTLY_REFRESH) ?
          (<Route
            exact={true}
            path={`${props.match.url}/marketnightlyrefresh`}
            render={() => (
              user && user.roles.includes(Roles.OPERATIONS_ADMIN) ? (
                <MarketNightlyRefresh />
              ) : (
                <Unauthorized />
              )
            )} />
          ) : null}
      <Route
        exact={true}
        path={`${props.match.url}/snowflakedatasharing`}
        render={() => {
            if (!!user && [Roles.SNOWFLAKE_DATA_SHARING_ADMIN, Roles.SNOWFLAKE_DATA_SHARING_VIEWER]
                .some(roles => user.roles.includes(roles))
            ) {
              return (
                <SnowflakeDataSharingContextProvider>
                  <SnowflakeDataSharing/>
                </SnowflakeDataSharingContextProvider>
              );
            }

            return <Unauthorized/>;
        }}
      />
      <Route
        exact={true}
        path={`${props.match.url}/project-performance`}
        render={() => (
          user && (user.roles.includes(Roles.OPERATIONS_USER) || user.roles.includes(Roles.OPERATIONS_ADMIN)) ? (
            <ProjectPerformancePage />
          ) : (
            <Unauthorized />
          )
        )}
      />
      <Route component={NotFound} />
    </Switch>
  );
}
